import React, { useState } from 'react';
import { Activity, TrendingUp, BarChart3, Target } from 'lucide-react';
import { BoxPlotData } from '../../types';

interface ComparativeBoxPlotProps {
  data: {
    spike: BoxPlotData[];
    volumeRatio: BoxPlotData[];
    thirtyMinHigh: BoxPlotData[];
    gap: BoxPlotData[];
  };
}

export const ComparativeBoxPlot: React.FC<ComparativeBoxPlotProps> = ({ data }) => {
  const [selectedMetric, setSelectedMetric] = useState<'spike' | 'volumeRatio' | 'thirtyMinHigh' | 'gap'>('spike');

  const metrics = [
    { id: 'spike' as const, label: 'Spike %', icon: Activity, color: 'blue' },
    { id: 'volumeRatio' as const, label: 'Volume Ratio', icon: TrendingUp, color: 'green' },
    { id: 'thirtyMinHigh' as const, label: '30 Min High %', icon: Target, color: 'yellow' },
    { id: 'gap' as const, label: 'Gap %', icon: BarChart3, color: 'purple' }
  ];

  const currentData = data[selectedMetric];
  const allValues = currentData.flatMap(d => [d.min, d.max]);
  const globalMin = Math.min(...allValues);
  const globalMax = Math.max(...allValues);
  const range = globalMax - globalMin;

  const getPosition = (value: number): number => {
    if (range === 0) return 50;
    return ((value - globalMin) / range) * 100;
  };

  const getColorClass = (metric: string) => {
    switch (metric) {
      case 'blue': return 'bg-blue-500';
      case 'green': return 'bg-green-500';
      case 'yellow': return 'bg-yellow-500';
      case 'purple': return 'bg-purple-500';
      default: return 'bg-blue-500';
    }
  };

  const getTextColorClass = (metric: string) => {
    switch (metric) {
      case 'blue': return 'text-blue-400';
      case 'green': return 'text-green-400';
      case 'yellow': return 'text-yellow-400';
      case 'purple': return 'text-purple-400';
      default: return 'text-blue-400';
    }
  };

  const selectedMetricConfig = metrics.find(m => m.id === selectedMetric)!;

  return (
    <div className="premium-chart-card">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h4 className="premium-chart-title">Box Plot Comparativo per Market Cap</h4>
          <p className="premium-chart-subtitle">Distribuzione statistica delle metriche</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {metrics.map(metric => {
            const MetricIcon = metric.icon;
            const isSelected = selectedMetric === metric.id;
            return (
              <button
                key={metric.id}
                onClick={() => setSelectedMetric(metric.id)}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                  isSelected
                    ? `${getColorClass(metric.color)}/20 ${getTextColorClass(metric.color)} border border-${metric.color}-500/50`
                    : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <MetricIcon className="h-3 w-3" />
                  <span>{metric.label}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-8">
        {currentData.map((boxPlot, index) => {
          const q1Pos = getPosition(boxPlot.q1);
          const medianPos = getPosition(boxPlot.median);
          const q3Pos = getPosition(boxPlot.q3);
          const minPos = getPosition(boxPlot.min);
          const maxPos = getPosition(boxPlot.max);
          const meanPos = getPosition(boxPlot.mean);

          return (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-white">{boxPlot.category}</span>
                <div className="flex items-center space-x-4 text-xs text-gray-400">
                  <span>Min: {boxPlot.min.toFixed(1)}</span>
                  <span>Q1: {boxPlot.q1.toFixed(1)}</span>
                  <span className="font-bold text-white">Med: {boxPlot.median.toFixed(1)}</span>
                  <span>Q3: {boxPlot.q3.toFixed(1)}</span>
                  <span>Max: {boxPlot.max.toFixed(1)}</span>
                  <span className={getTextColorClass(selectedMetricConfig.color)}>
                    Mean: {boxPlot.mean.toFixed(1)}
                  </span>
                </div>
              </div>

              <div className="relative h-16 bg-gray-800 rounded-lg">
                <div
                  className="absolute top-1/2 h-0.5 bg-gray-600"
                  style={{
                    left: `${minPos}%`,
                    right: `${100 - maxPos}%`,
                    transform: 'translateY(-50%)'
                  }}
                />

                <div
                  className={`absolute top-1/2 h-10 ${getColorClass(selectedMetricConfig.color)}/30 border-2 border-${selectedMetricConfig.color}-500 rounded`}
                  style={{
                    left: `${q1Pos}%`,
                    right: `${100 - q3Pos}%`,
                    transform: 'translateY(-50%)'
                  }}
                />

                <div
                  className={`absolute top-1/2 w-0.5 h-12 ${getColorClass(selectedMetricConfig.color)}`}
                  style={{
                    left: `${medianPos}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                />

                <div
                  className="absolute top-1/2 w-1 h-1 bg-white rounded-full"
                  style={{
                    left: `${meanPos}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                  title={`Media: ${boxPlot.mean.toFixed(1)}`}
                />

                <div
                  className="absolute top-1/2 w-0.5 h-10 bg-gray-600"
                  style={{
                    left: `${minPos}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                />

                <div
                  className="absolute top-1/2 w-0.5 h-10 bg-gray-600"
                  style={{
                    left: `${maxPos}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                />

                {boxPlot.outliers.slice(0, 20).map((outlier, oIndex) => {
                  const outlierPos = getPosition(outlier);
                  return (
                    <div
                      key={oIndex}
                      className="absolute top-1/2 w-1.5 h-1.5 bg-red-500 rounded-full"
                      style={{
                        left: `${outlierPos}%`,
                        transform: 'translate(-50%, -50%)'
                      }}
                      title={`Outlier: ${outlier.toFixed(1)}`}
                    />
                  );
                })}
              </div>

              {boxPlot.outliers.length > 0 && (
                <p className="text-xs text-red-400">
                  {boxPlot.outliers.length} outlier{boxPlot.outliers.length > 1 ? 's' : ''} rilevati
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 pt-4 border-t border-gray-700">
        <div className="flex items-center justify-between text-xs text-gray-400">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-gray-600 rounded"></div>
              <span>Min/Max</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 ${getColorClass(selectedMetricConfig.color)}/30 border border-${selectedMetricConfig.color}-500 rounded`}></div>
              <span>IQR (Q1-Q3)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-0.5 ${getColorClass(selectedMetricConfig.color)}`}></div>
              <span>Mediana</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
              <span>Media</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
              <span>Outliers</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
